import React from 'react';

import { useRouter } from 'next/router';
import { useMutation } from 'react-query';
import { useForm } from 'react-hook-form';

import ActionButton from '@core/components/actionbutton/ActionButton';
import UseToast from 'hooks/use-toast';
import UseModal from 'hooks/use-modal';

import {
	deleteAccountCheckService,
	deleteAccountConfirmService,
	deleteAccountRequestService
} from 'services/auth';

import { removeAuthorization } from 'utils/authentication';

import Modal from '@core/components/Modal/Modal';
import TextInput from '@core/components/textinput/TextInput';

import WarningIcon from 'assets/icons/warning-icon.svg?url';

import classes from './DeleteAccountModal.module.scss';

const DeleteAccountModal = ({ closeModal }) => {
	const router = useRouter();
	const {
		mutate: mutateRequest,
		error: requestError
	} = useMutation(deleteAccountRequestService, {
		onSuccess: () => {
			showRequestDelete();
		},
		onError: (err) => {
			if (err.response.status === 409) {
				showRequestDeleteError();
			} else {
				toast(err.response.data.data, 'alert');
			}
		}
	});
	const { mutate: mutateCheckCode, data: confirmToken } = useMutation(deleteAccountCheckService, {
		onError: (err) => {
			setError('code', { type: 'custom', message: err.response.data.data });
		},
		onSuccess: () => {
			showConfirmModal();
		}
	});
	const { mutate: mutateConfirm } = useMutation(deleteAccountConfirmService, {
		onSuccess: () => {
			removeAuthorization();
			router.replace('/deleted-account');
		}
	});

	const { ToastContainer, toast } = UseToast();
	const {
		show: showConfirm,
		destroyModal: destroyConfirmModal,
		showModal: showConfirmModal
	} = UseModal();

	const {
		show: RequestDeleteIsVisible,
		destroyModal: destroyRequestDeleteModal,
		showModal: showRequestDelete
	} = UseModal();

	const {
		show: RequestDeleteErrorIsVisible,
		destroyModal: destroyRequestDeleteErrorModal,
		showModal: showRequestDeleteError
	} = UseModal();

	const {
		show: showInputCode,
		destroyModal: destroyInputCodeModal,
		showModal: showInputCodeModal
	} = UseModal();

	const sendVerification = async () => {
		await mutateRequest();
		// showInputCodeModal();
	};

	const checkCode = (payload) => {
		mutateCheckCode(payload);
	};

	const confirmCode = () => {
		mutateConfirm(confirmToken.data);
	};

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors, isDirty, isValid }
	} = useForm({ mode: 'onChange' });

	return (
		<>
			<ToastContainer />
			<div className={`${classes['confirm-code-modal']}`}>
				<p>
					After deleting your account you wont have access to any of the Pharmuni content, or have
					access to your certificates.
				</p>
				<p>
					<b>All your data will be removed on our databases when you delete your account.</b>{' '}
				</p>
				
				<p>All of your certificates will be sent to you by email.</p>
				<p>You will receive a verification email before your account is deleted.</p>
				<div className="d-flex flex-column flex-md-row mt-md-3">
					<button onClick={closeModal} className={`${classes['cancel-btn']} order-2 order-md-1`}>
						Cancel deactivation
					</button>
					<button
						onClick={sendVerification}
						className={`${classes['confirm-btn']} order-1 order-md-2`}>
						Send verification email
					</button>
				</div>
			</div>
			<Modal title="Delete Account" show={showInputCode} closeHandler={destroyInputCodeModal}>
				<div className={`${classes['confirm-code-modal']}`}>
					<p>A 5 digit code has been sent to your email. Enter the code below.</p>
					<form
						onSubmit={handleSubmit(checkCode)}
						className={`${classes['check-code-form']} d-flex flex-column`}>
						<TextInput
							validation={{
								required: true,
								pattern: /[0-9]{5}/
							}}
							input={{
								type: 'text',
								name: 'code',
								id: 'code',
								placeholder: 'Enter code here'
							}}
							register={register}
							errors={errors}
						/>
						<button
							onClick={mutateCheckCode}
							className={`${classes['deleteConfirm']} w-50`}
							disabled={!isDirty || !isValid}>
							Delete account
						</button>
						<button className={classes['cancel-button']} onClick={closeModal}>
							Cancel
						</button>
					</form>
				</div>
			</Modal>
			<Modal header={false} show={showConfirm} closeHandler={destroyConfirmModal}>
				<div className={`${classes['confirm-modal']} container`}>
					<h1>Delete Account</h1>
					<p>Are you sure you want to Delete your account?</p>
					<div className={classes['modal-actions']}>
						<button onClick={destroyConfirmModal}>Cancel</button>
						<button
							data-test="confirm-delete"
							className={classes['confirm-button']}
							onClick={confirmCode}>
							Delete
						</button>
					</div>
				</div>
			</Modal>
			<Modal
				show={RequestDeleteIsVisible}
				closeHandler={destroyRequestDeleteModal}
				title={'Delete Account'}>
				<div
					className={`${classes['request-delete-modal']} d-flex flex-column align-items-center justify-content-center gap-4 p-3 p-md-4`}>
					<img src={WarningIcon} alt="warning-icon" />
					<h3>Your information will be deleted!</h3>
					<p>
						With Delete Account, all your information in <span>Pharmuni</span> and{' '}
						<span>Pharmuni Business</span> will be deleted.
					</p>
					<div className="w-100 d-flex justify-content-end mt-3 gap-2">
						<ActionButton kind="simple" onClick={destroyRequestDeleteModal}>
							Cancel
						</ActionButton>
						<ActionButton
							kind="alert"
							onClick={() => {
								destroyRequestDeleteModal();
								showInputCodeModal();
							}}>
							Delete Account
						</ActionButton>
					</div>
				</div>
			</Modal>
			<Modal
				show={RequestDeleteErrorIsVisible}
				closeHandler={destroyRequestDeleteErrorModal}
				title={'Delete Account'}>
				<div
					className={`${classes['request-delete-modal']} d-flex flex-column align-items-center justify-content-center gap-4 p-3 p-md-4`}>
					<img src={WarningIcon} alt="warning-icon" />
					<h3>{requestError?.response?.data?.message}</h3>
					<p>{requestError?.response?.data?.sub_message}</p>
					<div className="w-100 d-flex justify-content-end mt-3">
						<ActionButton kind="simple" onClick={destroyRequestDeleteErrorModal}>
							ok
						</ActionButton>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default DeleteAccountModal;
